import React, { useEffect, useMemo, useState, useCallback } from "react";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { useBrands } from "../contexts/BrandContext";
import { toast } from "sonner";
import { formatDateFr } from "../lib/dateFormat";
import {
  PackageMinus, Plus, Search, Filter, RefreshCw, Inbox, Trash2, X,
  Calendar as CalendarIcon, Clock, CheckCircle2, Hourglass, Ban, PenSquare, Download, Undo2,
  Building2, User2, Truck, PackagePlus, PackageX, Boxes, FileSpreadsheet, FileText, FileDown,
} from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import TimeInput24 from "./TimeInput24";
import { Textarea } from "./ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "./ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "./ui/select";
import SignaturePad from "./SignaturePad";
import EquipmentReturnTimeline from "./EquipmentReturnTimeline";
import EquipmentSiteReturnsView from "./EquipmentSiteReturnsView";
import StockRemovalReport from "./StockRemovalReport";

const STATUS_BADGE = {
  pending:              { label: "En attente collab",     cls: "bg-amber-50 text-amber-700 border-amber-200", Icon: Hourglass },
  awaiting_controller:  { label: "À contre-signer",       cls: "bg-sky-50 text-sky-700 border-sky-200",       Icon: PenSquare },
  confirmed:            { label: "Confirmée",             cls: "bg-emerald-50 text-emerald-700 border-emerald-200", Icon: CheckCircle2 },
  cancelled:            { label: "Annulée",               cls: "bg-zinc-50 text-zinc-600 border-zinc-200",    Icon: Ban },
};

const TRASH_RETENTION_DAYS = 30;

/** Jours restants avant purge définitive d'une ligne en corbeille. */
function daysUntilPurge(deletedAtIso) {
  if (!deletedAtIso) return null;
  const deletedAt = new Date(deletedAtIso);
  const purgeAt = new Date(deletedAt.getTime() + TRASH_RETENTION_DAYS * 24 * 60 * 60 * 1000);
  const diffMs = purgeAt.getTime() - Date.now();
  return Math.max(0, Math.ceil(diffMs / (24 * 60 * 60 * 1000)));
}

const ETAT_LABELS = {
  bon: "Bon état",
  use: "Usé",
  endommage: "Endommagé",
  inutilisable: "Inutilisable",
  perte: "Inutilisable", // legacy
};

const ETATS_SORTIE_DEFAULT = new Set(["endommage", "inutilisable", "perte"]);

const STOCK_ACTION_BADGE = {
  reintegrate: { label: "Stock réintégré", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  remove:      { label: "Sortie définitive", cls: "bg-rose-50 text-rose-700 border-rose-200" },
};

/**
 * Section "Restitution de matériels" — visible uniquement par admin / manager.
 * Permet d'émettre des demandes de restitution aux collaborateurs et de suivre
 * leur état (pending / confirmed / cancelled).
 */
export default function EquipmentReturnsTab() {
  const { user } = useAuth();
  const { brands, selectedBrandId } = useBrands();
  const isAdmin = user?.role === "admin";

  // Sous-onglet actif : "collab" (Restitution collaborateurs) | "site" (Enlèvements sites)
  const [scope, setScope] = useState("collab");

  const [returns, setReturns] = useState([]);
  const [collabs, setCollabs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState("awaiting_controller");
  const [collabFilter, setCollabFilter] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [exporting, setExporting] = useState(false);
  const [search, setSearch] = useState("");

  // Nouvelle demande
  const [openNew, setOpenNew] = useState(false);
  const [newForm, setNewForm] = useState({
    collaborateur_id: "",
    assignment_id: "",
    return_date: "",
    return_start_time: "09:00",
    return_end_time: "10:00",
    note: "",
  });
  const [collabAssignments, setCollabAssignments] = useState([]);
  const [loadingAssigns, setLoadingAssigns] = useState(false);

  // Suppression d'historique (admin only — toutes statuts confondus)
  const [deletingId, setDeletingId] = useState("");

  // Contre-signature (étape 2)
  const [signDialog, setSignDialog] = useState(null); // demande à contre-signer
  const [signForm, setSignForm] = useState({
    controller_signature: null,
    controller_name: "",
  });
  // Refus (étape 2 alternative)
  const [rejectMode, setRejectMode] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [submittingReject, setSubmittingReject] = useState(false);

  // Stock action (étape 3 — après contre-signature)
  const [stockDialog, setStockDialog] = useState(null); // demande à arbitrer
  const [stockForm, setStockForm] = useState({ action: "reintegrate", note: "" });
  const [submittingStock, setSubmittingStock] = useState(false);

  function openStockDialog(r) {
    // Suggestion par défaut basée sur l'état déclaré
    const defaultAction = ETATS_SORTIE_DEFAULT.has(r.etat) ? "remove" : "reintegrate";
    setStockForm({ action: defaultAction, note: "" });
    setStockDialog(r);
  }

  async function submitStockAction() {
    if (!stockDialog) return;
    setSubmittingStock(true);
    try {
      await api.put(`/equipment-returns/${stockDialog.id}/stock-action`, {
        action: stockForm.action,
        note: stockForm.note || null,
      });
      toast.success(
        stockForm.action === "remove"
          ? "Sortie définitive enregistrée — stock mis à jour."
          : "Matériel réintégré au stock."
      );
      setStockDialog(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de l'action stock.");
    } finally {
      setSubmittingStock(false);
    }
  }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter !== "all") params.status = statusFilter;
      if (selectedBrandId) params.brand_id = selectedBrandId;
      const res = await api.get("/equipment-returns", { params });
      setReturns(res.data || []);
    } catch {
      toast.error("Erreur lors du chargement des restitutions");
    } finally {
      setLoading(false);
    }
  }, [statusFilter, selectedBrandId]);

  useEffect(() => { load(); }, [load]);

  // Liste des collaborateurs (pour la sélection)
  const loadCollabs = useCallback(async () => {
    try {
      const params = selectedBrandId ? { brand_id: selectedBrandId } : {};
      const res = await api.get("/collaborateurs", { params });
      setCollabs(res.data || []);
    } catch {
      // Silencieux : la liste reste vide
    }
  }, [selectedBrandId]);
  useEffect(() => { loadCollabs(); }, [loadCollabs]);

  // Rafraîchissement temps réel + poll de secours
  useEffect(() => {
    const onNotif = (ev) => {
      const n = ev?.detail;
      if (!n) return;
      if (
        n.type === "equipment_return_requested"
        || n.type === "equipment_return_confirmed"
        || n.type === "equipment_return_collab_signed"
        || n.type === "equipment_return_validated"
        || n.type === "equipment_return_rejected_by_controller"
        || n.type === "equipment_return_cancelled"
      ) load();
    };
    const onUpdated = () => load();
    window.addEventListener("plania:notification", onNotif);
    window.addEventListener("plania:equipment-returns-updated", onUpdated);
    const pollId = setInterval(load, 60_000);
    return () => {
      window.removeEventListener("plania:notification", onNotif);
      window.removeEventListener("plania:equipment-returns-updated", onUpdated);
      clearInterval(pollId);
    };
  }, [load]);

  // Chargement des affectations actives du collaborateur sélectionné
  const loadCollabAssignments = useCallback(async (collabId) => {
    if (!collabId) {
      setCollabAssignments([]);
      return;
    }
    setLoadingAssigns(true);
    try {
      const res = await api.get("/equipment-collab-assignments", { params: { collaborateur_id: collabId } });
      const data = (res.data || []).filter((a) => a.status !== "returned" && a.status !== "rejected");
      setCollabAssignments(data);
    } catch {
      setCollabAssignments([]);
    } finally {
      setLoadingAssigns(false);
    }
  }, []);
  useEffect(() => {
    loadCollabAssignments(newForm.collaborateur_id);
  }, [newForm.collaborateur_id, loadCollabAssignments]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let base = returns;
    if (collabFilter && collabFilter !== "all") {
      base = base.filter((r) => r.collaborateur_id === collabFilter);
    }
    if (dateFrom) base = base.filter((r) => (r.created_at || "") >= dateFrom);
    if (dateTo) base = base.filter((r) => (r.created_at || "") < dateTo + "T99");
    if (!q) return base;
    return base.filter((r) => (
      (r.collab_name || "").toLowerCase().includes(q)
      || (r.designation_snapshot || "").toLowerCase().includes(q)
      || (r.ref_id_snapshot || "").toLowerCase().includes(q)
      || (r.note || "").toLowerCase().includes(q)
      || (r.return_date || "").includes(q)
    ));
  }, [returns, search, collabFilter, dateFrom, dateTo]);

  const exportCsv = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter && statusFilter !== "all") params.set("status", statusFilter);
      if (collabFilter && collabFilter !== "all") params.set("collaborateur_id", collabFilter);
      if (dateFrom) params.set("start_date", dateFrom);
      if (dateTo) {
        // borne haute exclue : on prend le lendemain pour rendre la sélection inclusive côté UI
        const d = new Date(dateTo);
        d.setDate(d.getDate() + 1);
        const pad = (n) => String(n).padStart(2, "0");
        const next = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
        params.set("end_date", next);
      }
      const res = await api.get(`/equipment-returns/export.csv?${params.toString()}`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const collabSlug = collabFilter !== "all"
        ? (collabs.find((c) => c.id === collabFilter)?.name || "collab")
            .toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")
        : "tous";
      const today = new Date().toISOString().slice(0, 10);
      a.download = `restitutions_${collabSlug}_${today}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Export CSV téléchargé");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de l'export CSV");
    } finally {
      setExporting(false);
    }
  };

  const resetNewForm = () => setNewForm({
    collaborateur_id: "",
    assignment_id: "",
    return_date: "",
    return_start_time: "09:00",
    return_end_time: "10:00",
    note: "",
  });

  const submitNew = async () => {
    if (!newForm.collaborateur_id) return toast.error("Sélectionnez un collaborateur.");
    if (!newForm.assignment_id) return toast.error("Sélectionnez un matériel à restituer.");
    if (!newForm.return_date) return toast.error("Indiquez la date de restitution.");
    if (!newForm.return_start_time || !newForm.return_end_time) return toast.error("Précisez le créneau horaire.");
    if (newForm.return_end_time <= newForm.return_start_time) return toast.error("L'heure de fin doit être après l'heure de début.");
    try {
      await api.post("/equipment-returns", newForm);
      toast.success("Demande de restitution envoyée au collaborateur");
      setOpenNew(false);
      resetNewForm();
      load();
      window.dispatchEvent(new CustomEvent("plania:equipment-returns-updated"));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const cancelReturn = async (id) => {
    if (!window.confirm("Annuler cette demande de restitution ?")) return;
    try {
      await api.delete(`/equipment-returns/${id}`);
      toast.success("Demande annulée");
      load();
      window.dispatchEvent(new CustomEvent("plania:equipment-returns-updated"));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  // Suppression définitive d'une ligne d'historique (admin only) — fonctionne
  // quel que soit le statut (pending / awaiting / confirmed / cancelled).
  // N'altère ni le stock ni les affectations en cours, ne notifie pas le collab :
  // c'est un nettoyage pur de l'historique.
  const deleteHistory = async (r) => {
    const lbl = r.designation_snapshot || "—";
    const who = r.collab_name || "—";
    const date = r.created_at ? new Date(r.created_at).toLocaleDateString("fr-FR") : "—";
    if (!window.confirm(
      `Mettre cette ligne d'historique de restitution dans la corbeille ?\n\n`
      + `• Collaborateur : ${who}\n`
      + `• Matériel : ${lbl}\n`
      + `• Créée le : ${date}\n\n`
      + `Elle restera restaurable pendant ${TRASH_RETENTION_DAYS} jours via la vue "Corbeille" avant suppression définitive automatique. `
      + `Aucun impact sur le stock ni les affectations en cours.`
    )) return;
    try {
      setDeletingId(r.id);
      await api.delete(`/equipment-returns/${r.id}/history`);
      toast.success("Ligne mise dans la corbeille (restaurable 30 jours)");
      await load();
      window.dispatchEvent(new CustomEvent("plania:equipment-returns-updated"));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de la suppression");
    } finally {
      setDeletingId("");
    }
  };

  // Restauration depuis la corbeille (admin only).
  const restoreFromTrash = async (r) => {
    if (!window.confirm(
      `Restaurer cette ligne depuis la corbeille ?\n\n`
      + `• Matériel : ${r.designation_snapshot || "—"}\n`
      + `• Collaborateur : ${r.collab_name || "—"}\n\n`
      + `La ligne réapparaîtra dans l'historique de restitution avec son statut d'origine.`
    )) return;
    try {
      setDeletingId(r.id);
      await api.post(`/equipment-returns/${r.id}/restore`);
      toast.success("Ligne restaurée depuis la corbeille");
      await load();
      window.dispatchEvent(new CustomEvent("plania:equipment-returns-updated"));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de la restauration");
    } finally {
      setDeletingId("");
    }
  };

  const awaitingControllerCount = returns.filter((r) => r.status === "awaiting_controller").length;
  const pendingCount = returns.filter((r) => r.status === "pending").length;

  // Préremplir le nom du contrôleur quand on ouvre la modale
  const openSignDialog = (r) => {
    setSignForm({
      controller_signature: null,
      controller_name: user?.name || user?.email || "",
    });
    setRejectMode(false);
    setRejectReason("");
    setSignDialog(r);
  };

  const submitControllerConfirm = async () => {
    if (!signDialog) return;
    if (!signForm.controller_signature) return toast.error("Votre signature est obligatoire.");
    try {
      await api.put(`/equipment-returns/${signDialog.id}/controller-confirm`, signForm);
      toast.success("Restitution contre-signée — validation définitive.");
      setSignDialog(null);
      load();
      window.dispatchEvent(new CustomEvent("plania:equipment-returns-updated"));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de la contre-signature");
    }
  };

  const submitControllerReject = async () => {
    if (!signDialog) return;
    const reason = rejectReason.trim();
    if (!reason) return toast.error("Le motif de refus est obligatoire.");
    if (reason.length < 5) return toast.error("Précisez le motif (au moins 5 caractères).");
    setSubmittingReject(true);
    try {
      await api.put(`/equipment-returns/${signDialog.id}/controller-reject`, {
        reason,
        controller_name: signForm.controller_name || undefined,
      });
      toast.success("Restitution refusée — le collaborateur a été notifié.");
      setSignDialog(null);
      setRejectMode(false);
      setRejectReason("");
      load();
      window.dispatchEvent(new CustomEvent("plania:equipment-returns-updated"));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors du refus");
    } finally {
      setSubmittingReject(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="equipment-returns-tab">
      {/* Rapport des sorties définitives (admin/manager) */}
      {(user?.role === "admin" || user?.role === "manager") && <StockRemovalReport />}

      <div className="bg-white border border-zinc-200 rounded-sm p-2 inline-flex gap-1" role="tablist" aria-label="Type de restitution">
        <button
          type="button" role="tab" aria-selected={scope === "collab"}
          onClick={() => setScope("collab")}
          data-testid="equip-return-scope-collab"
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold uppercase tracking-wider border rounded-sm transition ${scope === "collab" ? "bg-zinc-900 text-white border-zinc-900" : "border-zinc-200 text-zinc-700 hover:bg-zinc-50"}`}
        >
          <User2 size={12} /> Restitutions collaborateurs
        </button>
        <button
          type="button" role="tab" aria-selected={scope === "site"}
          onClick={() => setScope("site")}
          data-testid="equip-return-scope-site"
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold uppercase tracking-wider border rounded-sm transition ${scope === "site" ? "bg-zinc-900 text-white border-zinc-900" : "border-zinc-200 text-zinc-700 hover:bg-zinc-50"}`}
        >
          <Building2 size={12} /> Enlèvements sites
        </button>
      </div>

      {scope === "site" && (
        <EquipmentSiteReturnsView />
      )}

      {scope === "collab" && (
      <>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <p className="label-uppercase mb-1 inline-flex items-center gap-1.5">
            <PackageMinus size={11} /> Restitutions
          </p>
          <h2 className="font-display font-semibold text-xl text-zinc-900">
            Restitutions collaborateurs
          </h2>
          <p className="text-xs text-zinc-600 mt-1 font-mono">
            {statusFilter === "trash"
              ? `Corbeille — ${returns.length} ligne(s) supprimée(s), restaurables ${TRASH_RETENTION_DAYS} jours`
              : `Demandes émises aux collaborateurs · ${awaitingControllerCount} à contre-signer · ${pendingCount} en attente collab`}
          </p>
        </div>
        <div className="flex flex-col sm:flex-row sm:items-center gap-2 w-full sm:w-auto">
          <div className="flex items-center gap-2 flex-wrap">
            <Button
              type="button" variant="outline" size="sm" onClick={load} disabled={loading}
              data-testid="equip-return-refresh-btn"
            >
              <RefreshCw size={13} className={`mr-1.5 ${loading ? "animate-spin" : ""}`} />
              Rafraîchir
            </Button>
            <Button
              type="button" variant="outline" size="sm" onClick={exportCsv} disabled={exporting}
              data-testid="equip-return-export-csv-btn"
              title="Exporter l'historique des restitutions au format CSV (filtres pris en compte)"
            >
              <Download size={13} className={`mr-1.5 ${exporting ? "animate-pulse" : ""}`} />
              {exporting ? "Export…" : "Exporter CSV"}
            </Button>
          </div>
          <Button
            type="button" size="sm" onClick={() => setOpenNew(true)}
            data-testid="equip-return-new-btn"
            className="w-full sm:w-auto"
          >
            <Plus size={13} className="mr-1.5" /> Demander restitution
          </Button>
        </div>
      </div>

      {/* Filtres */}
      <div className="bg-white border border-zinc-200 rounded-sm p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <div>
          <Label className="label-uppercase mb-1.5 block inline-flex items-center gap-1.5">
            <Filter size={10} /> Statut
          </Label>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger data-testid="equip-return-status-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="awaiting_controller">À contre-signer</SelectItem>
              <SelectItem value="pending">En attente (collab)</SelectItem>
              <SelectItem value="confirmed">Confirmées</SelectItem>
              <SelectItem value="cancelled">Annulées</SelectItem>
              <SelectItem value="all">Toutes</SelectItem>
              {isAdmin && (
                <SelectItem value="trash" data-testid="equip-return-status-trash">
                  Corbeille (admin)
                </SelectItem>
              )}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="label-uppercase mb-1.5 block inline-flex items-center gap-1.5">
            <Filter size={10} /> Collaborateur
          </Label>
          <Select value={collabFilter} onValueChange={setCollabFilter}>
            <SelectTrigger data-testid="equip-return-collab-filter">
              <SelectValue placeholder="Tous" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les collaborateurs</SelectItem>
              {collabs.map((c) => (
                <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label className="label-uppercase mb-1.5 block inline-flex items-center gap-1.5">
              <CalendarIcon size={10} /> Du
            </Label>
            <Input
              type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
              data-testid="equip-return-date-from"
            />
          </div>
          <div>
            <Label className="label-uppercase mb-1.5 block inline-flex items-center gap-1.5">
              <CalendarIcon size={10} /> Au
            </Label>
            <Input
              type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
              data-testid="equip-return-date-to"
            />
          </div>
        </div>
        <div>
          <Label className="label-uppercase mb-1.5 block inline-flex items-center gap-1.5">
            <Search size={10} /> Recherche
          </Label>
          <Input
            placeholder="Nom, désignation, référence…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="equip-return-search-input"
          />
        </div>
      </div>

      {/* Liste */}
      <div className="bg-white border border-zinc-200 rounded-sm">
        {loading && filtered.length === 0 ? (
          <p className="text-sm text-zinc-500 font-mono py-10 text-center">Chargement…</p>
        ) : filtered.length === 0 ? (
          <div className="py-12 text-center" data-testid="equip-return-empty">
            <Inbox className="mx-auto text-zinc-300 mb-3" size={36} />
            <p className="text-sm text-zinc-500 font-mono">
              {statusFilter === "trash"
                ? "La corbeille est vide."
                : `Aucune demande de restitution ${statusFilter === "pending" ? "en attente" : ""} pour le moment.`}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-zinc-100">
            {filtered.map((r) => {
              const isTrashed = !!r.deleted_at;
              const badge = STATUS_BADGE[r.status] || STATUS_BADGE.pending;
              const brand = brands.find((b) => b.id === r.brand_id);
              const StatusIcon = badge.Icon;
              const daysLeft = isTrashed ? daysUntilPurge(r.deleted_at) : null;
              return (
                <div
                  key={r.id}
                  className={`p-4 flex items-start gap-3 flex-wrap ${isTrashed ? "bg-zinc-50/60" : ""}`}
                  data-testid={`equip-return-row-${r.id}`}
                >
                  <span
                    className="h-12 w-1 rounded-sm flex-shrink-0"
                    style={{ backgroundColor: isTrashed ? "#a1a1aa" : (brand?.color || "#0EA5A4") }}
                  />
                  <div className="flex-1 min-w-[240px]">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className="inline-flex items-center gap-1 text-[10px] uppercase font-semibold px-2 py-0.5 border rounded-sm tracking-wider bg-teal-50 border-teal-200 text-teal-800">
                        <PackageMinus size={10} /> Restitution
                      </span>
                      <p className="text-sm font-medium text-zinc-900">{r.collab_name || "—"}</p>
                      {isTrashed ? (
                        <span className="inline-flex items-center gap-1 text-[10px] uppercase font-semibold px-2 py-0.5 border rounded-sm tracking-wider bg-zinc-100 border-zinc-300 text-zinc-700">
                          <Trash2 size={10} /> Corbeille · Statut d&apos;origine&nbsp;:&nbsp;{badge.label}
                        </span>
                      ) : (
                        <span className={`inline-flex items-center gap-1 text-[10px] uppercase font-semibold px-2 py-0.5 border rounded-sm tracking-wider ${badge.cls}`}>
                          <StatusIcon size={10} /> {badge.label}
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-zinc-800 mt-1">
                      <b>{r.designation_snapshot || "—"}</b>
                      {r.ref_id_snapshot ? (
                        <span className="text-xs font-mono text-zinc-500 ml-2">({r.ref_id_snapshot})</span>
                      ) : null}
                      <span className="text-xs font-mono text-zinc-500 ml-2">· Qté {r.quantite_snapshot || 1}</span>
                    </p>
                    <p className="text-xs text-zinc-600 font-mono mt-1">
                      <CalendarIcon size={10} className="inline mr-1" />
                      Restitution prévue : <b>{r.return_date ? formatDateFr(r.return_date) : "—"}</b>
                      {" "}<Clock size={10} className="inline mx-1" />
                      {r.return_start_time} → {r.return_end_time}
                      {brand?.name ? <> · {brand.name}</> : null}
                    </p>
                    {r.note && (
                      <p className="text-xs text-zinc-600 mt-1 italic">« {r.note} »</p>
                    )}
                    {(r.status === "awaiting_controller" || r.status === "confirmed") && (
                      <p className="text-xs text-sky-700 font-mono mt-1">
                        Signé par le collaborateur le {r.actual_date ? formatDateFr(r.actual_date) : "—"} à {r.actual_time || "—"} · État déclaré : <b>{ETAT_LABELS[r.etat] || r.etat || "—"}</b>
                        {r.etat_note ? ` — ${r.etat_note}` : ""}
                      </p>
                    )}
                    {r.status === "confirmed" && (
                      <p className="text-xs text-emerald-700 font-mono mt-1">
                        Contre-signée par {r.controller_name || "—"}
                        {r.controller_signed_at ? ` le ${formatDateFr(r.controller_signed_at.slice(0, 10))}` : ""}
                        {" — validation définitive."}
                      </p>
                    )}
                    {r.stock_action && STOCK_ACTION_BADGE[r.stock_action] && (
                      <p className="text-xs font-mono mt-1" data-testid={`equip-return-stock-state-${r.id}`}>
                        <span className={`inline-flex items-center gap-1 text-[10px] uppercase font-semibold px-2 py-0.5 border rounded-sm tracking-wider ${STOCK_ACTION_BADGE[r.stock_action].cls}`}>
                          {r.stock_action === "remove" ? <PackageX size={10} /> : <PackagePlus size={10} />}
                          {STOCK_ACTION_BADGE[r.stock_action].label}
                        </span>
                        <span className="text-zinc-500 ml-2">
                          par {r.stock_action_by_name || "—"}
                          {r.stock_action_at ? ` le ${formatDateFr(r.stock_action_at.slice(0, 10))}` : ""}
                        </span>
                        {r.stock_action_note && (
                          <span className="text-zinc-600 italic ml-2">« {r.stock_action_note} »</span>
                        )}
                      </p>
                    )}
                    <p className="text-[11px] text-zinc-400 font-mono mt-1">
                      Émise par {r.requested_by_name || "—"}
                      {r.created_at ? ` le ${formatDateFr(r.created_at.slice(0, 10))}` : ""}
                    </p>
                    {isTrashed && (
                      <p className="text-[11px] text-zinc-600 font-mono mt-1 italic" data-testid={`equip-return-trash-meta-${r.id}`}>
                        <Trash2 size={10} className="inline mr-1" />
                        Supprimée par <b>{r.deleted_by_name || "—"}</b>
                        {r.deleted_at ? ` le ${formatDateFr(r.deleted_at.slice(0, 10))}` : ""}
                        {" · Purge automatique dans "}<b>{daysLeft ?? "—"} jour{(daysLeft ?? 0) > 1 ? "s" : ""}</b>
                      </p>
                    )}
                  </div>
                  {!isTrashed && r.status === "awaiting_controller" && (
                    <Button
                      type="button" size="sm"
                      onClick={() => openSignDialog(r)}
                      className="bg-sky-600 hover:bg-sky-700"
                      data-testid={`equip-return-controller-sign-${r.id}`}
                    >
                      <PenSquare size={13} className="mr-1.5" /> Contre-signer
                    </Button>
                  )}
                  {!isTrashed && r.status === "confirmed" && !r.stock_action && (user?.role === "admin" || user?.role === "manager") && (
                    <Button
                      type="button" size="sm"
                      onClick={() => openStockDialog(r)}
                      className="bg-teal-600 hover:bg-teal-700"
                      data-testid={`equip-return-stock-action-${r.id}`}
                      title="Réintégrer au stock ou sortir définitivement"
                    >
                      <Boxes size={13} className="mr-1.5" /> Action stock
                    </Button>
                  )}
                  {!isTrashed && isAdmin && (r.status === "pending" || r.status === "awaiting_controller") && (
                    <Button
                      type="button" variant="outline" size="sm"
                      onClick={() => cancelReturn(r.id)}
                      className="border-red-200 text-red-700 hover:bg-red-50"
                      data-testid={`equip-return-cancel-${r.id}`}
                    >
                      <Ban size={13} className="mr-1.5" /> Annuler
                    </Button>
                  )}
                  {!isTrashed && isAdmin && (
                    <Button
                      type="button" variant="outline" size="sm"
                      onClick={() => deleteHistory(r)}
                      disabled={deletingId === r.id}
                      className="h-8 px-2 text-red-700 border-red-200 hover:bg-red-50"
                      data-testid={`equip-return-delete-${r.id}`}
                      title="Mettre cette ligne dans la corbeille (restaurable 30 jours)"
                    >
                      <Trash2 size={13} />
                    </Button>
                  )}
                  {isTrashed && isAdmin && (
                    <Button
                      type="button" variant="outline" size="sm"
                      onClick={() => restoreFromTrash(r)}
                      disabled={deletingId === r.id}
                      className="border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                      data-testid={`equip-return-restore-${r.id}`}
                      title="Restaurer cette ligne depuis la corbeille"
                    >
                      <Undo2 size={13} className="mr-1.5" /> Restaurer
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Modale Nouvelle demande de restitution */}
      <Dialog
        open={openNew}
        onOpenChange={(v) => { setOpenNew(v); if (!v) resetNewForm(); }}
      >
        <DialogContent
          className="flex flex-col max-h-[90vh] p-0 sm:max-w-lg"
          data-testid="equip-return-new-dialog"
        >
          <DialogHeader className="px-6 pt-6 pb-4 border-b border-zinc-100">
            <DialogTitle>Nouvelle demande de restitution</DialogTitle>
            <DialogDescription className="sr-only">
              Émettre une demande de restitution à un collaborateur pour un matériel affecté.
            </DialogDescription>
          </DialogHeader>
          <div className="overflow-y-auto px-6 py-4 flex-1 space-y-4">
            <div>
              <Label className="label-uppercase mb-1.5 block">
                Collaborateur <span className="text-red-600">*</span>
              </Label>
              <Select
                value={newForm.collaborateur_id}
                onValueChange={(v) => setNewForm({ ...newForm, collaborateur_id: v, assignment_id: "" })}
              >
                <SelectTrigger data-testid="equip-return-collab-select">
                  <SelectValue placeholder="Sélectionner un collaborateur…" />
                </SelectTrigger>
                <SelectContent>
                  {collabs.filter((c) => c.is_active !== false).map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.name} — {c.email}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="label-uppercase mb-1.5 block">
                Matériel à restituer <span className="text-red-600">*</span>
                <span className="text-[10px] text-zinc-400 ml-2 normal-case">
                  (parmi les matériels affectés à ce collaborateur)
                </span>
              </Label>
              {!newForm.collaborateur_id ? (
                <p className="text-xs text-zinc-500 font-mono italic">
                  Sélectionnez d&apos;abord un collaborateur.
                </p>
              ) : loadingAssigns ? (
                <p className="text-xs text-zinc-500 font-mono">Chargement des matériels…</p>
              ) : collabAssignments.length === 0 ? (
                <p className="text-xs text-amber-700 font-mono">
                  Ce collaborateur n&apos;a aucun matériel actif à restituer.
                </p>
              ) : (
                <Select
                  value={newForm.assignment_id}
                  onValueChange={(v) => setNewForm({ ...newForm, assignment_id: v })}
                >
                  <SelectTrigger data-testid="equip-return-assignment-select">
                    <SelectValue placeholder="Sélectionner un matériel…" />
                  </SelectTrigger>
                  <SelectContent>
                    {collabAssignments.map((a) => (
                      <SelectItem key={a.id} value={a.id}>
                        {a.designation_snapshot || "—"}
                        {a.ref_id_snapshot ? ` (${a.ref_id_snapshot})` : ""}
                        {a.quantite ? ` — Qté ${a.quantite}` : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-3 sm:col-span-1">
                <Label className="label-uppercase mb-1.5 block">
                  Date <span className="text-red-600">*</span>
                </Label>
                <Input
                  type="date"
                  value={newForm.return_date}
                  onChange={(e) => setNewForm({ ...newForm, return_date: e.target.value })}
                  data-testid="equip-return-date-input"
                />
              </div>
              <div className="col-span-3 sm:col-span-1">
                <Label className="label-uppercase mb-1.5 block">
                  Début <span className="text-red-600">*</span>
                </Label>
                <TimeInput24
                  value={newForm.return_start_time}
                  onChange={(v) => setNewForm({ ...newForm, return_start_time: v })}
                  data-testid="equip-return-start-input"
                />
              </div>
              <div className="col-span-3 sm:col-span-1">
                <Label className="label-uppercase mb-1.5 block">
                  Fin <span className="text-red-600">*</span>
                </Label>
                <TimeInput24
                  value={newForm.return_end_time}
                  onChange={(v) => setNewForm({ ...newForm, return_end_time: v })}
                  data-testid="equip-return-end-input"
                />
              </div>
            </div>

            <div>
              <Label className="label-uppercase mb-1.5 block">
                Note <span className="text-zinc-400 normal-case">(optionnel)</span>
              </Label>
              <Textarea
                rows={3}
                value={newForm.note}
                onChange={(e) => setNewForm({ ...newForm, note: e.target.value })}
                placeholder="Motif, lieu de remise, précisions…"
                data-testid="equip-return-note-input"
              />
            </div>
          </div>
          <DialogFooter className="px-6 py-4 border-t border-zinc-100 bg-white">
            <Button variant="outline" onClick={() => setOpenNew(false)}>Annuler</Button>
            <Button onClick={submitNew} data-testid="equip-return-new-submit">
              Envoyer la demande
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modale Contre-signature (étape 2 — contrôleur) */}
      <Dialog
        open={!!signDialog}
        onOpenChange={(v) => { if (!v) setSignDialog(null); }}
      >
        <DialogContent
          className="flex flex-col max-h-[90vh] p-0 sm:max-w-lg"
          data-testid="equip-return-controller-dialog"
        >
          <DialogHeader className="px-6 pt-6 pb-4 border-b border-zinc-100">
            <DialogTitle>
              {rejectMode ? "Refuser la restitution" : "Contre-signer la restitution"}
            </DialogTitle>
            <DialogDescription className="sr-only">
              {rejectMode
                ? "Indiquez le motif du refus. Le collaborateur recevra une notification et devra re-confirmer."
                : "Le collaborateur a signé la restitution. Confirmez par votre contre-signature."}
            </DialogDescription>
          </DialogHeader>
          {signDialog && (
            <div className="overflow-y-auto px-6 py-4 flex-1 space-y-4">
              <EquipmentReturnTimeline
                request={signDialog}
                testidPrefix={`return-timeline-admin-${signDialog.id}`}
              />
              <div className={`${rejectMode ? "bg-red-50 border-red-200" : "bg-sky-50 border-sky-200"} border rounded-sm p-3 text-sm space-y-1`}>
                <p>
                  <b>{signDialog.designation_snapshot || "—"}</b>
                  <span className="text-xs font-mono text-zinc-600 ml-2">({signDialog.ref_id_snapshot || "—"})</span>
                  <span className="text-xs font-mono text-zinc-500 ml-2">· Qté {signDialog.quantite_snapshot || 1}</span>
                </p>
                <p className="text-xs font-mono text-zinc-700">
                  Collaborateur : <b>{signDialog.collab_name || "—"}</b>
                </p>
                <p className="text-xs font-mono text-zinc-700">
                  Signé le <b>{signDialog.actual_date ? formatDateFr(signDialog.actual_date) : "—"}</b>
                  {signDialog.actual_time ? <> à <b>{signDialog.actual_time}</b></> : null}
                  {" · État déclaré : "}<b>{ETAT_LABELS[signDialog.etat] || signDialog.etat || "—"}</b>
                </p>
                {signDialog.etat_note && (
                  <p className="text-xs text-zinc-600 italic">« {signDialog.etat_note} »</p>
                )}
                {signDialog.rejection_count > 0 && (
                  <p className="text-[11px] font-mono text-amber-700 pt-1 border-t border-zinc-200 mt-2">
                    Cette restitution a déjà été refusée {signDialog.rejection_count} fois.
                  </p>
                )}
              </div>

              {!rejectMode && signDialog.collab_signature && (
                <div>
                  <Label className="label-uppercase mb-1.5 block">Signature du collaborateur</Label>
                  <div className="border border-zinc-200 rounded-sm bg-white p-2 inline-block">
                    <img
                      src={signDialog.collab_signature}
                      alt="Signature collaborateur"
                      className="max-h-24"
                      data-testid="equip-return-collab-signature-preview"
                    />
                  </div>
                </div>
              )}

              {!rejectMode ? (
                <>
                  <div>
                    <Label className="label-uppercase mb-1.5 block">
                      Votre nom (contrôleur)
                    </Label>
                    <Input
                      value={signForm.controller_name}
                      onChange={(e) => setSignForm({ ...signForm, controller_name: e.target.value })}
                      placeholder="Nom & prénom"
                      data-testid="equip-return-controller-name-input"
                    />
                  </div>

                  <div>
                    <Label className="label-uppercase mb-1.5 block">
                      Votre signature <span className="text-red-600">*</span>
                    </Label>
                    <SignaturePad
                      value={signForm.controller_signature}
                      onSave={(url) => setSignForm((f) => ({ ...f, controller_signature: url }))}
                    />
                    <p className="text-[11px] text-zinc-500 font-mono mt-1">
                      Votre contre-signature valide définitivement la restitution : le matériel sera libéré du stock du collaborateur.
                    </p>
                  </div>
                </>
              ) : (
                <div>
                  <Label className="label-uppercase mb-1.5 block">
                    Motif du refus <span className="text-red-600">*</span>
                  </Label>
                  <Textarea
                    value={rejectReason}
                    onChange={(e) => setRejectReason(e.target.value)}
                    placeholder="Ex. : l'état déclaré 'bon' ne correspond pas — matériel rayé et écran fissuré. Merci de re-déclarer 'endommagé' avec description."
                    rows={5}
                    data-testid="equip-return-reject-reason"
                    className="resize-none"
                  />
                  <p className="text-[11px] text-zinc-500 font-mono mt-1">
                    Le collaborateur recevra une notification avec ce motif et devra re-confirmer la restitution avec les corrections appropriées.
                  </p>
                </div>
              )}
            </div>
          )}
          <DialogFooter className="px-6 py-4 border-t border-zinc-100 bg-white flex-wrap gap-2">
            {!rejectMode ? (
              <>
                <Button variant="outline" onClick={() => setSignDialog(null)}>Annuler</Button>
                <Button
                  variant="outline"
                  onClick={() => setRejectMode(true)}
                  className="border-red-200 text-red-700 hover:bg-red-50"
                  data-testid="equip-return-reject-toggle"
                >
                  <Ban size={13} className="mr-1.5" /> Refuser
                </Button>
                <Button
                  onClick={submitControllerConfirm}
                  disabled={!signForm.controller_signature}
                  className="bg-sky-600 hover:bg-sky-700"
                  data-testid="equip-return-controller-submit"
                >
                  <CheckCircle2 size={13} className="mr-1.5" /> Valider la contre-signature
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="outline"
                  onClick={() => { setRejectMode(false); setRejectReason(""); }}
                  data-testid="equip-return-reject-cancel"
                >
                  Retour
                </Button>
                <Button
                  onClick={submitControllerReject}
                  disabled={submittingReject || rejectReason.trim().length < 5}
                  className="bg-red-600 hover:bg-red-700 text-white"
                  data-testid="equip-return-reject-submit"
                >
                  <Ban size={13} className="mr-1.5" /> Confirmer le refus
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ----- Modale : Action stock (réintégration / sortie définitive) ----- */}
      <Dialog open={!!stockDialog} onOpenChange={(o) => !o && setStockDialog(null)}>
        <DialogContent className="max-w-md" data-testid="equip-return-stock-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Boxes size={18} className="text-teal-700" /> Action stock
            </DialogTitle>
            <DialogDescription className="text-xs">
              Décidez du sort du matériel après la contre-signature. Cette action est
              <b> irréversible</b>.
            </DialogDescription>
          </DialogHeader>
          {stockDialog && (
            <div className="space-y-3 text-sm">
              <div className="rounded-md border bg-zinc-50 p-3">
                <p className="font-medium text-zinc-900">{stockDialog.designation_snapshot || "—"}</p>
                <p className="text-xs text-zinc-600 font-mono mt-0.5">
                  Qté : <b>{stockDialog.quantite_snapshot || 1}</b>
                  {" · "}État déclaré : <b>{ETAT_LABELS[stockDialog.etat] || stockDialog.etat || "—"}</b>
                </p>
              </div>
              <div className="space-y-2">
                <label className="flex items-start gap-2 cursor-pointer p-2 rounded border hover:bg-emerald-50" data-testid="stock-action-reintegrate-radio">
                  <input
                    type="radio" name="stock_action" value="reintegrate"
                    checked={stockForm.action === "reintegrate"}
                    onChange={() => setStockForm((f) => ({ ...f, action: "reintegrate" }))}
                    className="mt-0.5"
                  />
                  <span>
                    <span className="inline-flex items-center gap-1 font-medium text-emerald-700">
                      <PackagePlus size={14} /> Réintégrer au stock
                    </span>
                    <span className="block text-xs text-zinc-600">
                      Matériel utilisable (Bon état ou Usé) — remis au stock disponible.
                    </span>
                  </span>
                </label>
                <label className="flex items-start gap-2 cursor-pointer p-2 rounded border hover:bg-rose-50" data-testid="stock-action-remove-radio">
                  <input
                    type="radio" name="stock_action" value="remove"
                    checked={stockForm.action === "remove"}
                    onChange={() => setStockForm((f) => ({ ...f, action: "remove" }))}
                    className="mt-0.5"
                  />
                  <span>
                    <span className="inline-flex items-center gap-1 font-medium text-rose-700">
                      <PackageX size={14} /> Sortie définitive du stock
                    </span>
                    <span className="block text-xs text-zinc-600">
                      Matériel inutilisable (Endommagé / Inutilisable) — décrémente la quantité totale.
                    </span>
                  </span>
                </label>
              </div>
              <div>
                <Label className="text-xs">Note / motif (optionnel)</Label>
                <Textarea
                  value={stockForm.note}
                  onChange={(e) => setStockForm((f) => ({ ...f, note: e.target.value }))}
                  placeholder="Ex : écran fissuré non réparable, jeté en DEEE…"
                  rows={2}
                  data-testid="stock-action-note-input"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setStockDialog(null)} disabled={submittingStock}>
              Annuler
            </Button>
            <Button
              type="button"
              onClick={submitStockAction}
              disabled={submittingStock}
              className={stockForm.action === "remove" ? "bg-rose-600 hover:bg-rose-700" : "bg-emerald-600 hover:bg-emerald-700"}
              data-testid="stock-action-confirm-btn"
            >
              {submittingStock ? "Enregistrement…" : "Confirmer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </>
      )}
    </div>
  );
}
